import {
  AddressBookItem,
  ContentItem,
  GroupMembershipLevel,
  UserDisplay,
} from "@coaching-culture/types";
import {
  Box,
  Button,
  Circle,
  DotMenu,
  Flex,
  IconButton,
  IconToast,
  Label,
  Loader,
  MiniToggle,
  Panel,
  PanelHeader,
  Rule,
  SortableTable,
  SortableTableColumn,
  Text,
  TextArea,
  useToast,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import axios from "axios";
import { AddressBookModal } from "components/AddressBookModal";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import ShareModal, { ShareConfig } from "components/ShareModal";
import UserProfileImage from "components/UserProfileImage";
import { groupBy, sortBy } from "lodash";
import {
  useAcceptGroupInvitation,
  useChangeMembershipLevel,
  useCloseGroup,
  useGroup,
  useInviteUsersToGroup,
  useLeaveGroup,
  useRemoveContentFromGroup,
  useRemoveMembership,
  useUpdateGroup,
} from "queries/users";
import { useState } from "react";
import {
  FaPen,
  FaShareAlt,
  FaTimes,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { useHistory, useParams, useRouteMatch } from "react-router";
import { Link } from "react-router-dom";

type EditGroupParams = {
  id: string;
};

const levelNames = ["Invited", "Member", "Admin", "Owner"];

const EditGroup = () => {
  const { id } = useParams<EditGroupParams>();
  const { data: group, isSuccess } = useGroup(id);
  const closeGroupMut = useCloseGroup();
  const leaveGroupMut = useLeaveGroup();
  const removeMembershipMut = useRemoveMembership(id);
  const changeMembershipLevelMut = useChangeMembershipLevel(id);
  const acceptGroupInvitationMut = useAcceptGroupInvitation(id);
  const removeContentMut = useRemoveContentFromGroup(id);
  const updateGroup = useUpdateGroup(id);
  const inviteUsersMut = useInviteUsersToGroup();
  const [showInvite, setShowInvite] = useState(false);
  const [shareConfig, setShareConfig] = useState<ShareConfig | null>(null);
  const history = useHistory();
  const isAdminRoute = useRouteMatch("/success/people/groups/:id") != null;
  const toast = useToast();
  const [user] = useUser();
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState("");

  const loading = !isSuccess;

  let userLevel: number = group?.members.find(
    (x) => x.id === user.id,
  )?.membershipLevel;

  if (userLevel == null) {
    userLevel = -1;
  }

  if (user.permissions.includes("org-admin")) {
    userLevel = 3;
  }

  const canEdit =
    userLevel > GroupMembershipLevel.Member ||
    user.permissions.includes("coordinator");

  const leaveGroup = async () => {
    if (window.confirm("Are you sure you want to leave the group?")) {
      await leaveGroupMut.mutateAsync(group.id);
      history.push("/solutions/my-groups");
    }
  };

  const closeGroup = async () => {
    if (window.confirm("Are you sure you want to close the group?")) {
      await closeGroupMut.mutateAsync(group.id);
      history.push("/solutions/my-groups");
    }
  };

  const shareModule = (id: string) => {
    setShareConfig({
      type: "module",
      itemId: null,
      targetId: id,
      targetType: "user",
    });
  };

  const removeMember = async (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to remove this user from the group?",
      )
    ) {
      await removeMembershipMut.mutateAsync(id);
    }
  };

  const onInviteSelect = async (items: AddressBookItem[]) => {
    setShowInvite(false);

    const userIds = items.map((x) => x.id);

    if (userIds.length === 0) {
      return;
    }

    try {
      const result = await inviteUsersMut.mutateAsync({
        groupId: group.id,
        userIds,
      });

      const parts = [];

      if (result.invited.length > 0) {
        parts.push(
          `${result.invited.length} invitation${
            result.invited.length === 1 ? "" : "s"
          } sent`,
        );
      }

      if (result.skipped > 0) {
        parts.push(`${result.skipped} already invited or a member`);
      }

      toast({
        content: (
          <IconToast
            icon={FaUserPlus}
            iconColor="primary"
            text={parts.join(", ")}
          />
        ),
      });
    } catch (err) {
      window.alert("Unable to send invitations right now.");
    }
  };

  const shareGroupModule = () => {
    setShareConfig({
      type: "module",
      itemId: null,
      targetId: group.id,
      targetType: "group",
    });
  };

  const setMembershipLevel = async (userId: string, level: number) => {
    await changeMembershipLevelMut.mutateAsync({ userId, level });
  };

  const getItemClick = (id: string) => (_: number, cmd: string | undefined) => {
    if (cmd === "share") {
      shareModule(id);
    } else if (cmd === "promote") {
      setMembershipLevel(id, 2);
    } else if (cmd === "demote") {
      setMembershipLevel(id, 1);
    } else if (cmd === "remove") {
      removeMember(id);
    }
  };

  const acceptInvitation = async () => {
    await acceptGroupInvitationMut.mutateAsync();
  };

  const rejectInvitation = async () => {
    await leaveGroup();
  };

  const onShareClose = () => {
    setShareConfig(null);
  };

  const changeName = async () => {
    const name = window.prompt("Enter new group name.");
    if (name) {
      try {
        await updateGroup.mutateAsync({ name });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          window.alert(err.response?.data.message.error);
        }
      }
    }
  };

  const removeModule = async (content: ContentItem) => {
    if (
      window.confirm(
        "Are you sure you want to remove that content from the group?",
      )
    ) {
      await removeContentMut.mutateAsync(content);
    }
  };

  const admins = group?.members.filter((x) => x.membershipLevel > 1) ?? [];
  const modules =
    group?.content.filter(
      (x) => x.type === "module" || x.type === "assessment",
    ) ?? [];
  const lessons =
    Object.entries(
      groupBy(
        group?.content.filter((y) => y.type === "lesson"),
        (x) => x.category.name,
      ),
    ) ?? [];

  const learncasts = group?.content.filter((x) => x.type === "learncast") ?? [];

  // Mirrors the API's rule for removing members (SEC 9.2): org-admins remove
  // anyone; coordinators and owners anyone but an owner; group admins only
  // members and pending invites.
  const canRemove = (level: GroupMembershipLevel) => {
    if (user.permissions.includes("org-admin")) {
      return true;
    }
    if (level === GroupMembershipLevel.Owner) {
      return false;
    }
    if (
      user.permissions.includes("coordinator") ||
      userLevel === GroupMembershipLevel.Owner
    ) {
      return true;
    }
    return (
      userLevel === GroupMembershipLevel.Admin &&
      level <= GroupMembershipLevel.Member
    );
  };

  const getItems = (
    u: UserDisplay & { membershipLevel: GroupMembershipLevel },
  ) => {
    return [
      user.id !== u.id &&
        u.membershipLevel > 0 &&
        (canEdit || userLevel > 1) && {
          label: "Share Content",
          cmd: "share",
        },
      canEdit &&
        user.id !== u.id &&
        u.membershipLevel === 1 && {
          label: "Promote to Admin",
          cmd: "promote",
        },
      canEdit &&
        user.id !== u.id &&
        u.membershipLevel === 2 && {
          label: "Demote to Member",
          cmd: "demote",
        },
      canRemove(u.membershipLevel) && {
        label: "Remove",
        cmd: "remove",
      },
    ].filter(Boolean);
  };

  const cols: SortableTableColumn<(typeof group.members)[number]>[] = [
    {
      name: "name",
      label: "Name",
    },
    {
      name: "membershipLevel",
      label: "Level",
      format: (x) => (
        <Label
          size="small"
          color={x.membershipLevel === 0 ? "warning" : undefined}
        >
          {levelNames[x.membershipLevel]}
        </Label>
      ),
      sortFormat: (x) => x.membershipLevel,
    },
    {
      name: "",
      label: "",
      props: {
        style: {
          textAlign: "right",
        },
      },
      format: (x) =>
        x.id !== user.id && (
          <DotMenu items={getItems(x)} onItemClick={getItemClick(x.id)} />
        ),
    },
  ];

  const saveDescription = async () => {
    await updateGroup.mutateAsync({
      description,
    });

    setEditingDescription(false);
  };

  return (
    <CenterColumn>
      {loading ? (
        <Loader />
      ) : (
        <>
          <AddressBookModal
            isOpen={showInvite}
            features={["users"]}
            excludeUserIds={group.members.map((x) => x.id)}
            onSelect={onInviteSelect}
            onCancel={() => setShowInvite(false)}
          />

          {shareConfig && (
            <ShareModal onClose={onShareClose} config={shareConfig} />
          )}
          <PageHeader
            text={group.name}
            backUrl={
              isAdminRoute ? "/success/people/groups" : "/solutions/my-groups"
            }
          >
            {canEdit && (
              <>
                <Button onClick={changeName} icon={FaPen}>
                  Change Group Name
                </Button>
                <Button onClick={shareGroupModule} icon={FaShareAlt}>
                  Assign Content
                </Button>
                <Button
                  onClick={() => setShowInvite(true)}
                  icon={FaUserPlus}
                  variant="primary"
                >
                  Invite Users
                </Button>
              </>
            )}
          </PageHeader>
          {userLevel === 0 && (
            <Panel mb={3} p={3}>
              <Flex justifyContent="space-between" alignItems="center">
                <Text mb={0} fontSize={4} fontWeight={600}>
                  You've been invited to join this group
                </Text>
                <Flex alignItems="center">
                  <Button color="positive" onClick={acceptInvitation} mr={1}>
                    Accept
                  </Button>
                  <Button color="danger" onClick={rejectInvitation}>
                    Reject
                  </Button>
                </Flex>
              </Flex>
            </Panel>
          )}
          <Flex flexDirection={["column", "row"]}>
            <Flex
              flexDirection="column"
              width={["100%", 280]}
              alignItems="stretch"
              mr={[0, 3]}
              order={[1, 0]}
              style={{ gap: 12 }}
            >
              <Panel>
                <PanelHeader>
                  <Text fontWeight={600}>Group Info</Text>
                </PanelHeader>
                <Box p={3}>
                  {!group.membersHidden && (
                    <Text mb={1} fontWeight={600}>
                      <Text as="strong" color="primary" fontSize={4}>
                        {group.members.length}{" "}
                      </Text>
                      member{group.members.length === 1 ? "" : "s"}
                    </Text>
                  )}
                  {canEdit && (
                    <MiniToggle
                      mb={2}
                      label="Private member list"
                      value={group.privateMembers}
                      onChange={(val) =>
                        updateGroup.mutate({ privateMembers: val })
                      }
                    />
                  )}
                  {editingDescription ? (
                    <Box>
                      <TextArea
                        mb={1}
                        value={description}
                        onChange={(ev) => setDescription(ev.target.value)}
                        autoFocus
                        height={200}
                      />
                      <Flex style={{ gap: 6 }}>
                        <Button
                          variant="primary"
                          onClick={saveDescription}
                          disabled={updateGroup.isLoading}
                        >
                          Save
                        </Button>
                        <Button
                          flex={1}
                          onClick={() => setEditingDescription(false)}
                        >
                          Cancel
                        </Button>
                      </Flex>
                    </Box>
                  ) : (
                    <Flex
                      alignItems={"flex-start"}
                      justifyContent="space-between"
                    >
                      <Text style={{ whiteSpace: "pre-wrap" }}>
                        {group.description
                          ? group.description
                          : "No Description Given"}
                      </Text>
                      <IconButton
                        icon={FaPen}
                        onClick={() => {
                          setDescription(group?.description ?? "");
                          setEditingDescription(true);
                        }}
                        color="body"
                      />
                    </Flex>
                  )}
                </Box>
              </Panel>
              <Panel>
                <PanelHeader>
                  <Text fontWeight={600}>Modules & Assessments</Text>
                </PanelHeader>
                <Flex p={3} flexDirection="column" alignItems="stretch">
                  {modules.length === 0 ? (
                    <Text color="textMuted">None assigned</Text>
                  ) : (
                    modules.map((x, i, self) => (
                      <Flex
                        alignItems="center"
                        key={x.type + x.id}
                        mb={i === self.length - 1 ? 0 : 2}
                      >
                        <Circle
                          image={
                            process.env.REACT_APP_STATIC_ROOT + "/" + x.icon
                          }
                          mr={2}
                        />
                        <Flex flexDirection="column" flex="1">
                          <Link
                            to={
                              x.type === "module"
                                ? `/solutions/mindset/modules/${x.slug}`
                                : `/solutions/assessments/modules/${x.slug}`
                            }
                          >
                            <Text fontWeight={600}>{x.name}</Text>
                          </Link>
                          <Text fontSize={2}>
                            {x.type === "module"
                              ? "Mindset Module"
                              : "Assessment"}
                          </Text>
                        </Flex>
                        {userLevel >= 2 ? (
                          <Flex>
                            <IconButton
                              icon={FaTimes}
                              color={"body"}
                              onClick={() => removeModule(x)}
                            />
                          </Flex>
                        ) : null}
                      </Flex>
                    ))
                  )}
                </Flex>
              </Panel>
              <Panel>
                <PanelHeader>
                  <Text fontWeight={600}>Learncasts</Text>
                </PanelHeader>
                <Flex p={3} flexDirection="column" alignItems="stretch">
                  {learncasts.length === 0 ? (
                    <Text color="textMuted">None assigned</Text>
                  ) : (
                    learncasts.map((x, i, self) => (
                      <Flex
                        alignItems="center"
                        key={x.type + x.id}
                        mb={i === self.length - 1 ? 0 : 2}
                      >
                        <Circle
                          image={
                            process.env.REACT_APP_STATIC_ROOT + "/" + x.icon
                          }
                          mr={2}
                        />
                        <Flex flexDirection="column" flex="1">
                          <Link to={`/solutions/learncast/modules/${x.slug}`}>
                            <Text fontWeight={600}>{x.name}</Text>
                          </Link>
                          <Text fontSize={2}>LearnCast</Text>
                        </Flex>
                        {userLevel >= 2 ? (
                          <Flex>
                            <IconButton
                              icon={FaTimes}
                              color={"body"}
                              onClick={() => removeModule(x)}
                            />
                          </Flex>
                        ) : null}
                      </Flex>
                    ))
                  )}
                </Flex>
              </Panel>
              <Panel>
                <PanelHeader>
                  <Text fontWeight={600}>Lessons</Text>
                </PanelHeader>
                <Flex p={3} flexDirection="column" alignItems="stretch">
                  {lessons.length === 0 && (
                    <Text color="textMuted">None assigned</Text>
                  )}
                  {lessons.map((cat, j) => (
                    <>
                      <Text mb={3} fontWeight={600} fontSize={3}>
                        {cat[0]}
                      </Text>

                      {sortBy(cat[1], "orderIndex").map((x, i, self) => (
                        <Flex
                          alignItems="center"
                          key={x.type + x.id}
                          mb={i === self.length - 1 ? 0 : 2}
                        >
                          <Circle
                            image={
                              process.env.REACT_APP_STATIC_ROOT + "/" + x.icon
                            }
                            mr={2}
                          />
                          <Flex flexDirection="column" flex="1">
                            <Link to={`/solutions/lessons/modules/${x.slug}`}>
                              <Text fontWeight={600}>{x.name}</Text>
                            </Link>
                            <Text fontSize={2}>Lesson</Text>
                          </Flex>
                          {userLevel >= 2 ? (
                            <Flex>
                              <IconButton
                                icon={FaTimes}
                                color={"body"}
                                onClick={() => removeModule(x)}
                              />
                            </Flex>
                          ) : null}
                        </Flex>
                      ))}
                      {j !== lessons.length - 1 && <Rule />}
                    </>
                  ))}
                </Flex>
              </Panel>
              <Panel mb={userLevel > -1 ? 3 : 0}>
                <PanelHeader>
                  <Text fontWeight={600}>Group Admins</Text>
                </PanelHeader>
                <Flex p={3} flexDirection="column" alignItems="stretch">
                  {group.membersHidden
                    ? "Private"
                    : admins.length === 0
                      ? "No admins"
                      : admins.map((x, i, self) => (
                          <Flex
                            alignItems="center"
                            key={x.id}
                            mb={i === self.length - 1 ? 0 : 2}
                          >
                            <UserProfileImage
                              profileImage={null}
                              name={x.name}
                              mr={2}
                              size="small"
                            />
                            <Flex flexDirection="column">
                              <Text fontWeight={600}>{x.name}</Text>
                              <Text fontSize={2}>
                                {levelNames[x.membershipLevel]}
                              </Text>
                            </Flex>
                          </Flex>
                        ))}
                </Flex>
              </Panel>
              {userLevel > -1 && (
                <Panel mb={[3, 0]}>
                  <PanelHeader>
                    <Text fontWeight={600}>Actions</Text>
                  </PanelHeader>
                  <Flex p={3} flexDirection="column" alignItems="stretch">
                    {userLevel === 3 ? (
                      <Button onClick={closeGroup} color="danger">
                        Close Group
                      </Button>
                    ) : (
                      <Button onClick={leaveGroup} color="danger">
                        Leave Group
                      </Button>
                    )}
                  </Flex>
                </Panel>
              )}
            </Flex>
            <Panel flex="1" order={[0, 1]} mb={[3, 0]}>
              <PanelHeader>
                <Text fontWeight={600}>Members</Text>
              </PanelHeader>
              {group.membersHidden ? (
                <Box p={3}>
                  <Text color="textMuted">
                    The member list for this group is private.
                  </Text>
                </Box>
              ) : (
                <Box>
                  <SortableTable
                    columns={cols}
                    data={group.members}
                    emptyIcon={FaUsers}
                    emptyMessage="This group has no members yet."
                  />
                </Box>
              )}
            </Panel>
          </Flex>
        </>
      )}
    </CenterColumn>
  );
};

export default EditGroup;
